// Property-panel editors for the Tier 6 marketing blocks (bento grid,
// marquee, counter, before/after, carousel, team, steps, comparison table,
// social links, lottie, cookie consent).
//
// Kept out of `blockEditors.tsx` (already ~3k lines) the same way the
// runtime keeps these renderers in `marketingRenderers.tsx`; the shared field
// styles and sub-editors are imported from there so the panel looks the same.
//
// Two rules every editor here follows:
//   1. Never drop what is saved. Every update spreads the existing object,
//      a select shows a value it does not recognise as "(unknown) …" rather
//      than silently displaying the first option, and a field the current
//      mode ignores stays visible while it holds data.
//   2. Show the RUNTIME's defaults. An unset number shows the renderer's
//      fallback as its placeholder, and an unset checkbox shows the state the
//      renderer actually uses (`marketingRenderers.tsx`).

import { useEffect, useState, type JSX } from 'react'
import { Plus } from 'lucide-react'
import { newId } from './blocks'
import { AssetPickerField } from './AssetPicker'
import {
  FIELD_LABEL, INPUT, HINT, SUBSECTION, TWO_COL,
  AlignPicker, ColumnsCountPicker, BackgroundEditor, OptionalButtonEditor,
  ItemControls, moveItem, IconField, ButtonSpecEditor,
} from './blockEditors'
import type {
  Block, BentoGridBlock, BentoCell, MarqueeBlock, MarqueeItem, CounterBlock, CounterItem,
  BeforeAfterBlock, CarouselBlock, CarouselSlide, TeamBlock, TeamMember, StepsBlock, ProcessStep,
  ComparisonTableBlock, ComparisonColumn, ComparisonRow, SocialLinksBlock, SocialLink, SocialNetwork,
  LottieBlock, CookieConsentBlock, ConsentCategory, MediaAspect, FloatingButtonBlock,
} from './types'

// ─── Local helpers ───────────────────────────────────

const TEXTAREA: React.CSSProperties = { ...INPUT, resize: 'vertical', fontFamily: 'inherit' }
const CHECK_ROW: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9125rem', cursor: 'pointer' }
const LIST: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 8 }
// A field the current mode ignores but that still holds data: shown, not
// hidden, so switching modes never looks like it lost the author's work.
const DIMMED: React.CSSProperties = { opacity: 0.55 }

/** Empty text ⇒ unset, so the renderer's own fallback applies. */
function optText(v: string): string | undefined { return v === '' ? undefined : v }

/** Replace item `i` with a shallow merge — siblings and other fields survive. */
function patchAt<T>(arr: T[], i: number, patch: Partial<T>): T[] {
  const next = [...arr]
  next[i] = { ...arr[i], ...patch }
  return next
}

/**
 * Optional number. Empty ⇒ `undefined` (the runtime default, shown as the
 * placeholder). Values outside min/max are not committed — the same rule as
 * the shared `NumberInput`, which cannot represent "unset".
 */
function OptNumber({ value, placeholder, min, max, integer = true, onChange }: {
  value: number | undefined
  placeholder?: string | number
  min?: number
  max?: number
  integer?: boolean
  onChange: (n: number | undefined) => void
}) {
  return (
    <input
      type="number" min={min} max={max} step={integer ? 1 : 'any'}
      value={value ?? ''}
      placeholder={placeholder === undefined ? undefined : String(placeholder)}
      onChange={(e) => {
        const raw = e.target.value
        if (raw.trim() === '') { onChange(undefined); return }
        const n = Number(raw)
        if (!Number.isFinite(n)) return
        if (min !== undefined && n < min) return
        if (max !== undefined && n > max) return
        onChange(integer ? Math.round(n) : n)
      }}
      style={INPUT}
    />
  )
}

/**
 * A REQUIRED number (a counter's value). Typed as text so a half-typed "12."
 * or "-" is not snapped back by a controlled number input; only a value that
 * parses is committed, and leaving the box empty restores the last good one.
 */
function DraftNumber({ value, placeholder, onChange }: {
  value: number | undefined
  placeholder?: string
  onChange: (n: number) => void
}) {
  const shown = value === undefined || value === null ? '' : String(value)
  const [text, setText] = useState(shown)
  useEffect(() => {
    // Re-sync on an outside change (undo, another panel), but leave a draft
    // that already parses to the same number alone — that is what keeps a
    // trailing "." or "0" while the author is still typing.
    setText((t) => (t.trim() !== '' && Number(t) === value ? t : shown))
  }, [value, shown])
  return (
    <input
      type="text" inputMode="decimal"
      value={text}
      placeholder={placeholder}
      onChange={(e) => {
        const t = e.target.value
        setText(t)
        const n = Number(t)
        if (t.trim() !== '' && Number.isFinite(n)) onChange(n)
      }}
      onBlur={() => {
        if (text.trim() === '' || !Number.isFinite(Number(text))) setText(shown)
      }}
      style={INPUT}
    />
  )
}

/**
 * A select over known options. `fallback` is the runtime default shown while
 * the field is unset; a saved value outside the options is listed as
 * "(unknown) …" so it stays visible and is only replaced on purpose.
 */
function SelectField<T extends string | number>({ value, fallback, options, onChange }: {
  value: T | undefined
  fallback: T
  options: readonly (readonly [T, string])[]
  onChange: (v: T) => void
}) {
  const current = value ?? fallback
  const known = options.some(([v]) => v === current)
  return (
    <select
      value={String(current)}
      onChange={(e) => {
        const hit = options.find(([v]) => String(v) === e.target.value)
        if (hit) onChange(hit[0])
      }}
      style={INPUT}
    >
      {!known && <option value={String(current)}>(unknown) {String(current)}</option>}
      {options.map(([v, label]) => <option key={String(v)} value={String(v)}>{label}</option>)}
    </select>
  )
}

/** Checkbox whose unset state is the runtime's default, not `false`. */
function Check({ value, fallback, label, onChange }: {
  value: boolean | undefined
  fallback: boolean
  label: string
  onChange: (v: boolean) => void
}) {
  return (
    <label style={CHECK_ROW}>
      <input type="checkbox" checked={value ?? fallback} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  )
}

/** The header row of one list entry: its label plus move/remove controls. */
function ItemHeader({ label, i, count, onMove, onRemove }: {
  label: string
  i: number
  count: number
  onMove: (dir: -1 | 1) => void
  onRemove: () => void
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>{label}</label>
      <ItemControls
        onUp={() => onMove(-1)}
        onDown={() => onMove(1)}
        onRemove={onRemove}
        canUp={i > 0} canDown={i < count - 1}
      />
    </div>
  )
}

function AddButton({ label, onAdd }: { label: string; onAdd: () => void }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onAdd() }}
      className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
    ><Plus size={13} /> {label}</button>
  )
}

/** Eyebrow / heading / subheading — the shared `SectionHead` of the runtime. */
function SectionHeadFields({ eyebrow, heading, subheading, showEyebrow = true, showSubheading = true, onChange }: {
  eyebrow?: string
  heading?: string
  subheading?: string
  showEyebrow?: boolean
  showSubheading?: boolean
  onChange: (patch: { eyebrow?: string; heading?: string; subheading?: string }) => void
}) {
  return (
    <>
      {(showEyebrow || !!eyebrow) && (
        <div><label style={FIELD_LABEL}>Eyebrow</label>
          <input value={eyebrow || ''} onChange={(e) => onChange({ eyebrow: optText(e.target.value) })} style={INPUT} />
        </div>
      )}
      <div><label style={FIELD_LABEL}>Heading</label>
        <input value={heading || ''} onChange={(e) => onChange({ heading: optText(e.target.value) })} style={INPUT} />
      </div>
      {(showSubheading || !!subheading) && (
        <div><label style={FIELD_LABEL}>Subheading</label>
          <textarea rows={2} value={subheading || ''} onChange={(e) => onChange({ subheading: optText(e.target.value) })} style={TEXTAREA} />
        </div>
      )}
    </>
  )
}

// Same order + labels as the runtime's NETWORK_LABEL (marketingRenderers.tsx).
const SOCIAL_NETWORKS: readonly (readonly [SocialNetwork, string])[] = [
  ['x', 'X'], ['facebook', 'Facebook'], ['instagram', 'Instagram'], ['linkedin', 'LinkedIn'],
  ['youtube', 'YouTube'], ['github', 'GitHub'], ['tiktok', 'TikTok'], ['threads', 'Threads'],
  ['discord', 'Discord'], ['whatsapp', 'WhatsApp'], ['telegram', 'Telegram'],
  ['mastodon', 'Mastodon'], ['pinterest', 'Pinterest'], ['reddit', 'Reddit'],
  ['email', 'Email'], ['website', 'Website'], ['rss', 'RSS'],
]
// What share mode offers when `networks` is unset (SocialLinksRenderer).
const DEFAULT_SHARE_NETWORKS: SocialNetwork[] = ['x', 'facebook', 'linkedin', 'whatsapp', 'email']

/** A list of follow links — the social_links block and each team member. */
function SocialLinkList({ links, onChange, addLabel = 'Add link' }: {
  links: SocialLink[]
  onChange: (next: SocialLink[]) => void
  addLabel?: string
}) {
  return (
    <div style={LIST}>
      {links.map((l, i) => (
        <div key={l.id} style={SUBSECTION}>
          <ItemHeader
            label={`Link ${i + 1}`} i={i} count={links.length}
            onMove={(d) => onChange(moveItem(links, i, d))}
            onRemove={() => onChange(links.filter((_, k) => k !== i))}
          />
          <SelectField
            value={l.network} fallback="website" options={SOCIAL_NETWORKS}
            onChange={(network) => onChange(patchAt(links, i, { network }))}
          />
          <input
            value={l.url} placeholder={l.network === 'email' ? 'mailto:hello@example.com' : 'https://…'}
            onChange={(e) => onChange(patchAt(links, i, { url: e.target.value }))} style={INPUT}
          />
          <input
            value={l.label || ''} placeholder="Accessible label (optional)"
            onChange={(e) => onChange(patchAt(links, i, { label: optText(e.target.value) }))} style={INPUT}
          />
        </div>
      ))}
      <AddButton label={addLabel} onAdd={() => onChange([...links, { id: newId('so'), network: 'website', url: '' }])} />
    </div>
  )
}

// ─── Bento grid ──────────────────────────────────────

export function BentoGridEditor({ block, project, onChange }: { block: BentoGridBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<BentoGridBlock>) => onChange({ ...block, ...patch })
  const cells = block.cells || []
  const cols = block.columns || 4
  const setCell = (i: number, patch: Partial<BentoCell>) => upd({ cells: patchAt(cells, i, patch) })
  return (
    <>
      <SectionHeadFields
        eyebrow={block.eyebrow} heading={block.heading} subheading={block.subheading}
        onChange={(p) => upd(p)}
      />
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Columns</label>
          <SelectField
            value={block.columns} fallback={4}
            options={[[3, '3 columns'], [4, '4 columns']] as const}
            onChange={(n) => upd({ columns: n })}
          />
        </div>
        <div><label style={FIELD_LABEL}>Gap (px)</label>
          <OptNumber value={block.gap} placeholder={16} min={0} max={96} onChange={(gap) => upd({ gap })} />
        </div>
      </div>
      <p style={HINT}>Desktop track count. Tablets show 2 columns and phones 1, whatever the spans.</p>
      <div style={LIST}>
        {cells.map((c, i) => (
          <div key={c.id} style={SUBSECTION}>
            <ItemHeader
              label={`Cell ${i + 1}`} i={i} count={cells.length}
              onMove={(d) => upd({ cells: moveItem(cells, i, d) })}
              onRemove={() => upd({ cells: cells.filter((_, k) => k !== i) })}
            />
            <div style={TWO_COL}>
              <div><label style={FIELD_LABEL}>Width</label>
                <SelectField
                  value={c.span} fallback={1}
                  options={[[1, '1 column'], [2, '2 columns'], [3, '3 columns'], [4, '4 columns']] as const}
                  onChange={(span) => setCell(i, { span })}
                />
              </div>
              <div><label style={FIELD_LABEL}>Height</label>
                <SelectField
                  value={c.rowSpan} fallback={1}
                  options={[[1, '1 row'], [2, '2 rows']] as const}
                  onChange={(rowSpan) => setCell(i, { rowSpan })}
                />
              </div>
            </div>
            {(c.span || 1) > cols && (
              <p style={HINT}>Wider than the grid — drawn {cols} columns wide.</p>
            )}
            <input value={c.eyebrow || ''} placeholder="Eyebrow (optional)" onChange={(e) => setCell(i, { eyebrow: optText(e.target.value) })} style={INPUT} />
            <input value={c.title || ''} placeholder="Title" onChange={(e) => setCell(i, { title: optText(e.target.value) })} style={INPUT} />
            <textarea rows={2} value={c.body || ''} placeholder="Body" onChange={(e) => setCell(i, { body: optText(e.target.value) })} style={TEXTAREA} />
            <div><label style={FIELD_LABEL}>Icon</label>
              <IconField value={c.icon} onChange={(icon) => setCell(i, { icon })} />
            </div>
            <div><label style={FIELD_LABEL}>Image</label>
              <AssetPickerField project={project} value={c.imageUrl} accept="image/*" onChange={(url) => setCell(i, { imageUrl: optText(url) })} />
              <p style={HINT}>Drawn faintly behind the cell's text.</p>
            </div>
            <div><label style={FIELD_LABEL}>Alignment</label>
              <AlignPicker value={c.align || 'left'} onChange={(align) => setCell(i, { align })} />
            </div>
            <BackgroundEditor value={c.background} project={project} onChange={(background) => setCell(i, { background })} />
            <OptionalButtonEditor value={c.cta} label="Button" onChange={(cta) => setCell(i, { cta })} />
          </div>
        ))}
        <AddButton label="Add cell" onAdd={() => upd({ cells: [...cells, { id: newId('bc'), span: 1, title: 'New tile' }] })} />
      </div>
    </>
  )
}

// ─── Marquee ─────────────────────────────────────────

export function MarqueeEditor({ block, project, onChange }: { block: MarqueeBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<MarqueeBlock>) => onChange({ ...block, ...patch })
  const items = block.items || []
  const setItem = (i: number, patch: Partial<MarqueeItem>) => upd({ items: patchAt(items, i, patch) })
  return (
    <>
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Seconds per pass</label>
          <OptNumber value={block.speed} placeholder={30} min={1} max={600} onChange={(speed) => upd({ speed })} />
        </div>
        <div><label style={FIELD_LABEL}>Direction</label>
          <SelectField
            value={block.direction} fallback="left"
            options={[['left', 'Scroll left'], ['right', 'Scroll right']] as const}
            onChange={(direction) => upd({ direction })}
          />
        </div>
      </div>
      <p style={HINT}>Lower is faster. Visitors who prefer reduced motion see a static, wrapped row.</p>
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Separator</label>
          <input value={block.separator || ''} placeholder="None (e.g. •)" onChange={(e) => upd({ separator: optText(e.target.value) })} style={INPUT} />
        </div>
        <div><label style={FIELD_LABEL}>Logo height (px)</label>
          <OptNumber value={block.itemHeight} placeholder={32} min={8} max={240} onChange={(itemHeight) => upd({ itemHeight })} />
        </div>
      </div>
      <Check value={block.pauseOnHover} fallback label="Pause on hover" onChange={(pauseOnHover) => upd({ pauseOnHover })} />
      <Check value={block.fadeEdges} fallback label="Fade the left and right edges" onChange={(fadeEdges) => upd({ fadeEdges })} />
      <div style={LIST}>
        {items.map((it, i) => (
          <div key={it.id} style={SUBSECTION}>
            <ItemHeader
              label={`Item ${i + 1}`} i={i} count={items.length}
              onMove={(d) => upd({ items: moveItem(items, i, d) })}
              onRemove={() => upd({ items: items.filter((_, k) => k !== i) })}
            />
            <input value={it.text || ''} placeholder={it.imageUrl ? 'Alt text' : 'Text'} onChange={(e) => setItem(i, { text: optText(e.target.value) })} style={INPUT} />
            <AssetPickerField project={project} value={it.imageUrl} accept="image/*" onChange={(url) => setItem(i, { imageUrl: optText(url) })} />
            {it.imageUrl && <p style={HINT}>With an image set, the text above becomes the image's alt text.</p>}
            <input value={it.href || ''} placeholder="Link (optional)" onChange={(e) => setItem(i, { href: optText(e.target.value) })} style={INPUT} />
          </div>
        ))}
        <AddButton label="Add item" onAdd={() => upd({ items: [...items, { id: newId('mq'), text: 'New item' }] })} />
      </div>
    </>
  )
}

// ─── Counter ─────────────────────────────────────────

export function CounterEditor({ block, onChange }: { block: CounterBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<CounterBlock>) => onChange({ ...block, ...patch })
  const items = block.items || []
  const setItem = (i: number, patch: Partial<CounterItem>) => upd({ items: patchAt(items, i, patch) })
  return (
    <>
      <div><label style={FIELD_LABEL}>Heading</label>
        <input value={block.heading || ''} onChange={(e) => upd({ heading: optText(e.target.value) })} style={INPUT} />
      </div>
      <div><label style={FIELD_LABEL}>Columns</label>
        <ColumnsCountPicker value={block.columns || 4} max={6} onChange={(columns) => upd({ columns })} />
      </div>
      <div><label style={FIELD_LABEL}>Alignment</label>
        <AlignPicker value={block.align || 'center'} onChange={(align) => upd({ align })} />
      </div>
      <div><label style={FIELD_LABEL}>Count-up duration (ms)</label>
        <OptNumber value={block.duration} placeholder={1600} min={1} max={60000} onChange={(duration) => upd({ duration })} />
      </div>
      <Check value={block.separator} fallback label="Thousands separators (12,000)" onChange={(separator) => upd({ separator })} />
      <div style={LIST}>
        {items.map((it, i) => (
          <div key={it.id} style={SUBSECTION}>
            <ItemHeader
              label={`Figure ${i + 1}`} i={i} count={items.length}
              onMove={(d) => upd({ items: moveItem(items, i, d) })}
              onRemove={() => upd({ items: items.filter((_, k) => k !== i) })}
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 8 }}>
              <input value={it.prefix || ''} placeholder="Prefix" onChange={(e) => setItem(i, { prefix: optText(e.target.value) })} style={INPUT} />
              <DraftNumber value={it.value} placeholder="Value" onChange={(value) => setItem(i, { value })} />
              <input value={it.suffix || ''} placeholder="Suffix" onChange={(e) => setItem(i, { suffix: optText(e.target.value) })} style={INPUT} />
            </div>
            <input value={it.label} placeholder="Label" onChange={(e) => setItem(i, { label: e.target.value })} style={INPUT} />
            <div><label style={FIELD_LABEL}>Decimal places</label>
              <OptNumber value={it.decimals} placeholder="Auto (from the value)" min={0} max={6} onChange={(decimals) => setItem(i, { decimals })} />
            </div>
          </div>
        ))}
        <AddButton label="Add figure" onAdd={() => upd({ items: [...items, { id: newId('cnt'), value: 100, label: 'Label' }] })} />
      </div>
    </>
  )
}

// ─── Before / after ──────────────────────────────────

const MEDIA_ASPECTS: readonly (readonly [MediaAspect, string])[] = [
  ['16:9', '16:9 (wide)'], ['21:9', '21:9 (cinema)'], ['3:2', '3:2'], ['4:3', '4:3'],
  ['1:1', '1:1 (square)'], ['3:4', '3:4 (portrait)'], ['2:3', '2:3 (portrait)'], ['auto', 'Auto'],
]

export function BeforeAfterEditor({ block, project, onChange }: { block: BeforeAfterBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<BeforeAfterBlock>) => onChange({ ...block, ...patch })
  const initial = Math.max(0, Math.min(100, block.initial ?? 50))
  return (
    <>
      <div><label style={FIELD_LABEL}>Before image</label>
        <AssetPickerField project={project} value={block.beforeUrl} accept="image/*" onChange={(beforeUrl) => upd({ beforeUrl })} />
      </div>
      <div><label style={FIELD_LABEL}>After image</label>
        <AssetPickerField project={project} value={block.afterUrl} accept="image/*" onChange={(afterUrl) => upd({ afterUrl })} />
      </div>
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Before label</label>
          <input value={block.beforeLabel || ''} onChange={(e) => upd({ beforeLabel: optText(e.target.value) })} style={INPUT} />
        </div>
        <div><label style={FIELD_LABEL}>After label</label>
          <input value={block.afterLabel || ''} onChange={(e) => upd({ afterLabel: optText(e.target.value) })} style={INPUT} />
        </div>
      </div>
      <p style={HINT}>Labels are drawn on the images; leave blank for none.</p>
      <div><label style={FIELD_LABEL}>Caption</label>
        <input value={block.caption || ''} onChange={(e) => upd({ caption: optText(e.target.value) })} style={INPUT} />
      </div>
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Aspect ratio</label>
          <SelectField value={block.aspect} fallback="16:9" options={MEDIA_ASPECTS} onChange={(aspect) => upd({ aspect })} />
        </div>
        <div><label style={FIELD_LABEL}>Divider</label>
          <SelectField
            value={block.orientation} fallback="horizontal"
            options={[['horizontal', 'Left / right'], ['vertical', 'Top / bottom']] as const}
            onChange={(orientation) => upd({ orientation })}
          />
        </div>
      </div>
      <div><label style={FIELD_LABEL}>Starting position ({initial}%)</label>
        <input
          type="range" min={0} max={100} value={initial}
          onChange={(e) => upd({ initial: parseInt(e.target.value, 10) })}
          style={{ width: '100%' }}
        />
      </div>
    </>
  )
}

// ─── Carousel ────────────────────────────────────────

export function CarouselEditor({ block, project, onChange }: { block: CarouselBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<CarouselBlock>) => onChange({ ...block, ...patch })
  const slides = block.slides || []
  const setSlide = (i: number, patch: Partial<CarouselSlide>) => upd({ slides: patchAt(slides, i, patch) })
  return (
    <>
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Height (px)</label>
          <OptNumber value={block.height} placeholder={380} min={80} max={1600} onChange={(height) => upd({ height })} />
        </div>
        <div><label style={FIELD_LABEL}>Interval (ms)</label>
          <OptNumber value={block.autoplayInterval} placeholder={5000} min={0} max={120000} onChange={(autoplayInterval) => upd({ autoplayInterval })} />
        </div>
      </div>
      <p style={HINT}>Intervals under 1500 ms run at 1500 ms.</p>
      <Check value={block.autoplay} fallback label="Autoplay" onChange={(autoplay) => upd({ autoplay })} />
      <Check value={block.showArrows} fallback label="Show arrows" onChange={(showArrows) => upd({ showArrows })} />
      <Check value={block.showDots} fallback label="Show dots" onChange={(showDots) => upd({ showDots })} />
      {/* `perView` has no control: the runtime cross-fades one slide at a
          time and does not honour it yet. A saved value is kept (every update
          spreads the block) and called out so it is not a silent no-op. */}
      {block.perView !== undefined && block.perView !== 1 && (
        <p style={HINT}>Saved "{block.perView} slides per view" is kept, but the carousel currently shows one slide at a time.</p>
      )}
      <div style={LIST}>
        {slides.map((sl, i) => (
          <div key={sl.id} style={SUBSECTION}>
            <ItemHeader
              label={`Slide ${i + 1}`} i={i} count={slides.length}
              onMove={(d) => upd({ slides: moveItem(slides, i, d) })}
              onRemove={() => upd({ slides: slides.filter((_, k) => k !== i) })}
            />
            <input value={sl.title || ''} placeholder="Title" onChange={(e) => setSlide(i, { title: optText(e.target.value) })} style={INPUT} />
            <textarea rows={2} value={sl.body || ''} placeholder="Body" onChange={(e) => setSlide(i, { body: optText(e.target.value) })} style={TEXTAREA} />
            <div><label style={FIELD_LABEL}>Image</label>
              <AssetPickerField project={project} value={sl.imageUrl} accept="image/*" onChange={(url) => setSlide(i, { imageUrl: optText(url) })} />
              <p style={HINT}>Fills the slide; text over an image gets a dark backing for contrast.</p>
            </div>
            <OptionalButtonEditor value={sl.cta} label="Button" onChange={(cta) => setSlide(i, { cta })} />
            <BackgroundEditor value={sl.background} project={project} onChange={(background) => setSlide(i, { background })} />
          </div>
        ))}
        <AddButton label="Add slide" onAdd={() => upd({ slides: [...slides, { id: newId('sl'), title: 'New slide' }] })} />
      </div>
      {slides.length === 0 && <p style={HINT}>A carousel with no slides renders nothing.</p>}
    </>
  )
}

// ─── Team ────────────────────────────────────────────

export function TeamEditor({ block, project, onChange }: { block: TeamBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<TeamBlock>) => onChange({ ...block, ...patch })
  const members = block.members || []
  const setMember = (i: number, patch: Partial<TeamMember>) => upd({ members: patchAt(members, i, patch) })
  return (
    <>
      <SectionHeadFields
        eyebrow={block.eyebrow} heading={block.heading} subheading={block.subheading}
        onChange={(p) => upd(p)}
      />
      <div><label style={FIELD_LABEL}>Columns</label>
        <ColumnsCountPicker value={block.columns || 4} max={6} onChange={(columns) => upd({ columns })} />
      </div>
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Photo shape</label>
          <SelectField
            value={block.shape} fallback="circle"
            options={[['circle', 'Circle'], ['rounded', 'Rounded'], ['square', 'Square']] as const}
            onChange={(shape) => upd({ shape })}
          />
        </div>
        <div><label style={FIELD_LABEL}>Alignment</label>
          <AlignPicker value={block.align || 'left'} onChange={(align) => upd({ align })} />
        </div>
      </div>
      <div style={LIST}>
        {members.map((m, i) => (
          <div key={m.id} style={SUBSECTION}>
            <ItemHeader
              label={`Member ${i + 1}`} i={i} count={members.length}
              onMove={(d) => upd({ members: moveItem(members, i, d) })}
              onRemove={() => upd({ members: members.filter((_, k) => k !== i) })}
            />
            <div style={TWO_COL}>
              <input value={m.name} placeholder="Name" onChange={(e) => setMember(i, { name: e.target.value })} style={INPUT} />
              <input value={m.role || ''} placeholder="Role" onChange={(e) => setMember(i, { role: optText(e.target.value) })} style={INPUT} />
            </div>
            <textarea rows={2} value={m.bio || ''} placeholder="Short bio (optional)" onChange={(e) => setMember(i, { bio: optText(e.target.value) })} style={TEXTAREA} />
            <div><label style={FIELD_LABEL}>Photo</label>
              <AssetPickerField project={project} value={m.photoUrl} accept="image/*" onChange={(url) => setMember(i, { photoUrl: optText(url) })} />
              {!m.photoUrl && <p style={HINT}>Without a photo the member's initial is shown.</p>}
            </div>
            <div><label style={FIELD_LABEL}>Profile links</label>
              <SocialLinkList
                links={m.links || []}
                onChange={(links) => setMember(i, { links: links.length ? links : undefined })}
              />
            </div>
          </div>
        ))}
        <AddButton label="Add member" onAdd={() => upd({ members: [...members, { id: newId('tm'), name: 'New member' }] })} />
      </div>
    </>
  )
}

// ─── Steps ───────────────────────────────────────────

export function StepsEditor({ block, onChange }: { block: StepsBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<StepsBlock>) => onChange({ ...block, ...patch })
  const steps = block.steps || []
  const numbered = block.numbered !== false
  const setStep = (i: number, patch: Partial<ProcessStep>) => upd({ steps: patchAt(steps, i, patch) })
  return (
    <>
      <SectionHeadFields
        eyebrow={block.eyebrow} heading={block.heading} subheading={block.subheading}
        onChange={(p) => upd(p)}
      />
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Layout</label>
          <SelectField
            value={block.layout} fallback="horizontal"
            options={[['horizontal', 'Horizontal'], ['vertical', 'Vertical']] as const}
            onChange={(layout) => upd({ layout })}
          />
        </div>
        <div><label style={FIELD_LABEL}>Header alignment</label>
          <AlignPicker value={block.align || 'left'} onChange={(align) => upd({ align })} />
        </div>
      </div>
      <div><label style={FIELD_LABEL}>Style</label>
        <SelectField
          value={block.variant} fallback="cards"
          options={[['cards', 'Numbered process'], ['progress', 'Progress line (Cart ✓ — Details — Payment)']] as const}
          onChange={(variant) => upd({ variant: variant === 'cards' ? undefined : variant })}
        />
      </div>
      {block.variant === 'progress' ? (
        <div style={SUBSECTION}>
          <label style={FIELD_LABEL}>Current step</label>
          <select value={block.current ?? 0} onChange={(e) => upd({ current: Number(e.target.value) })} style={INPUT}>
            {steps.map((st, i) => <option key={st.id} value={i}>{i + 1}. {st.title}</option>)}
          </select>
          <label style={FIELD_LABEL}>Move on when a page variable is set</label>
          {(block.advance || []).map((r, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 6 }}>
              <input value={r.whenVar} placeholder="variable, e.g. orderId"
                onChange={(e) => upd({ advance: patchAt(block.advance || [], i, { whenVar: e.target.value.trim() }) })} style={INPUT} />
              <select value={r.current} onChange={(e) => upd({ advance: patchAt(block.advance || [], i, { current: Number(e.target.value) }) })} style={INPUT}>
                {steps.map((st, k) => <option key={st.id} value={k}>{k + 1}. {st.title}</option>)}
              </select>
              <button type="button" onClick={() => upd({ advance: (block.advance || []).filter((_, k) => k !== i) })}
                style={{ ...INPUT, width: 'auto', cursor: 'pointer' }} aria-label="Remove rule">×</button>
            </div>
          ))}
          <AddButton label="Add rule" onAdd={() => upd({ advance: [...(block.advance || []), { whenVar: '', current: Math.min(steps.length - 1, (block.current ?? 0) + 1) }] })} />
          <p style={HINT}>The last rule whose variable holds a value wins — e.g. a checkout&apos;s orderId moves the line to Payment.</p>
        </div>
      ) : (
        <>
          <Check value={block.numbered} fallback label="Numbered" onChange={(n) => upd({ numbered: n })} />
          <p style={HINT}>Step icons replace the numbers only when Numbered is off.</p>
        </>
      )}
      <div style={LIST}>
        {steps.map((st, i) => (
          <div key={st.id} style={SUBSECTION}>
            <ItemHeader
              label={`Step ${i + 1}`} i={i} count={steps.length}
              onMove={(d) => upd({ steps: moveItem(steps, i, d) })}
              onRemove={() => upd({ steps: steps.filter((_, k) => k !== i) })}
            />
            <input value={st.title} placeholder="Title" onChange={(e) => setStep(i, { title: e.target.value })} style={INPUT} />
            <textarea rows={2} value={st.description || ''} placeholder="Description" onChange={(e) => setStep(i, { description: optText(e.target.value) })} style={TEXTAREA} />
            <div style={numbered ? DIMMED : undefined}>
              <label style={FIELD_LABEL}>Icon</label>
              <IconField value={st.icon} onChange={(icon) => setStep(i, { icon })} />
            </div>
          </div>
        ))}
        <AddButton label="Add step" onAdd={() => upd({ steps: [...steps, { id: newId('st'), title: 'New step' }] })} />
      </div>
    </>
  )
}

// ─── Comparison table ────────────────────────────────

type CellKind = 'yes' | 'no' | 'text'
const CELL_KINDS: readonly (readonly [CellKind, string])[] = [['yes', '✓ Yes'], ['no', '✗ No'], ['text', 'Text']]

/** Pad a row's cells to `n` so a cell can be addressed by column index. */
function padCells(values: (string | boolean)[] | undefined, n: number): (string | boolean)[] {
  const next = [...(values || [])]
  while (next.length < n) next.push('')
  return next
}

export function ComparisonTableEditor({ block, onChange }: { block: ComparisonTableBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<ComparisonTableBlock>) => onChange({ ...block, ...patch })
  const columns = block.columns || []
  const rows = block.rows || []
  const setColumn = (i: number, patch: Partial<ComparisonColumn>) => upd({ columns: patchAt(columns, i, patch) })
  const setRow = (i: number, patch: Partial<ComparisonRow>) => upd({ rows: patchAt(rows, i, patch) })

  // Cells are positional (one per column, in column order), so every column
  // change is mirrored onto every row's `values` — otherwise adding, removing
  // or moving a column would shift the ticks under the wrong plan.
  const addColumn = () => {
    const at = columns.length
    upd({
      columns: [...columns, { id: newId('cc'), label: `Column ${at + 1}` }],
      rows: rows.map((r) => {
        const values = padCells(r.values, at)
        values.splice(at, 0, false)
        return { ...r, values }
      }),
    })
  }
  const removeColumn = (ci: number) => upd({
    columns: columns.filter((_, k) => k !== ci),
    rows: rows.map((r) => {
      if (!r.values || r.values.length <= ci) return r
      const values = [...r.values]
      values.splice(ci, 1)
      return { ...r, values }
    }),
  })
  const moveColumn = (ci: number, dir: -1 | 1) => {
    const j = ci + dir
    if (j < 0 || j >= columns.length) return
    upd({
      columns: moveItem(columns, ci, dir),
      rows: rows.map((r) => ({ ...r, values: moveItem(padCells(r.values, Math.max(ci, j) + 1), ci, dir) })),
    })
  }
  const setCell = (ri: number, ci: number, v: string | boolean) => {
    const values = padCells(rows[ri].values, ci + 1)
    values[ci] = v
    setRow(ri, { values })
  }

  return (
    <>
      <SectionHeadFields
        heading={block.heading} subheading={block.subheading} showEyebrow={false}
        onChange={(p) => upd(p)}
      />
      <Check value={block.stickyHeader} fallback label="Keep the column headings visible while scrolling" onChange={(stickyHeader) => upd({ stickyHeader })} />

      <label style={{ ...FIELD_LABEL, marginTop: 4 }}>Columns</label>
      <div style={LIST}>
        {columns.map((c, i) => (
          <div key={c.id} style={SUBSECTION}>
            <ItemHeader
              label={`Column ${i + 1}`} i={i} count={columns.length}
              onMove={(d) => moveColumn(i, d)}
              onRemove={() => removeColumn(i)}
            />
            <div style={TWO_COL}>
              <input value={c.label} placeholder="Label" onChange={(e) => setColumn(i, { label: e.target.value })} style={INPUT} />
              <input value={c.badge || ''} placeholder="Badge (optional)" onChange={(e) => setColumn(i, { badge: optText(e.target.value) })} style={INPUT} />
            </div>
            <label style={CHECK_ROW}>
              <input type="checkbox" checked={!!c.highlighted} onChange={(e) => setColumn(i, { highlighted: e.target.checked })} />
              Highlight this column
            </label>
          </div>
        ))}
        <AddButton label="Add column" onAdd={addColumn} />
      </div>

      <label style={{ ...FIELD_LABEL, marginTop: 4 }}>Rows</label>
      <div style={LIST}>
        {rows.map((r, ri) => {
          const extra = (r.values?.length || 0) - columns.length
          return (
            <div key={r.id} style={SUBSECTION}>
              <ItemHeader
                label={`Row ${ri + 1}`} i={ri} count={rows.length}
                onMove={(d) => upd({ rows: moveItem(rows, ri, d) })}
                onRemove={() => upd({ rows: rows.filter((_, k) => k !== ri) })}
              />
              <div style={TWO_COL}>
                <input value={r.label} placeholder="Feature" onChange={(e) => setRow(ri, { label: e.target.value })} style={INPUT} />
                <input value={r.group || ''} placeholder="Group heading (optional)" onChange={(e) => setRow(ri, { group: optText(e.target.value) })} style={INPUT} />
              </div>
              {columns.map((c, ci) => {
                const v = r.values?.[ci]
                const kind: CellKind = v === true ? 'yes' : v === false ? 'no' : 'text'
                return (
                  <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '72px 84px 1fr', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: '0.8425rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.label}>
                      {c.label || `Column ${ci + 1}`}
                    </span>
                    <SelectField
                      value={kind} fallback="text" options={CELL_KINDS}
                      onChange={(k) => setCell(ri, ci, k === 'yes' ? true : k === 'no' ? false : (typeof v === 'string' ? v : ''))}
                    />
                    {kind === 'text'
                      ? <input
                          value={v === undefined || v === null ? '' : String(v)}
                          placeholder="Blank = —"
                          onChange={(e) => setCell(ri, ci, e.target.value)}
                          style={INPUT}
                        />
                      : <span />}
                  </div>
                )
              })}
              {extra > 0 && (
                <p style={HINT}>{extra} more value{extra === 1 ? '' : 's'} beyond the last column — kept, not shown.</p>
              )}
            </div>
          )
        })}
        <AddButton
          label="Add row"
          onAdd={() => upd({ rows: [...rows, { id: newId('cr'), label: 'New feature', values: columns.map(() => false) }] })}
        />
      </div>
    </>
  )
}

// ─── Social links ────────────────────────────────────

export function SocialLinksEditor({ block, onChange }: { block: SocialLinksBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<SocialLinksBlock>) => onChange({ ...block, ...patch })
  const mode = block.mode || 'follow'
  const links = block.links || []
  const networks = block.networks || DEFAULT_SHARE_NETWORKS
  const showLinks = mode === 'follow' || links.length > 0
  const showShare = mode === 'share' || (block.networks?.length ?? 0) > 0 || !!block.shareTitle
  // Saved networks the editor does not know are listed too, so they can be
  // seen and unticked instead of riding along invisibly.
  const unknownNets = networks.filter((n) => !SOCIAL_NETWORKS.some(([k]) => k === n))

  const toggleNetwork = (n: SocialNetwork, on: boolean) => {
    // Start from what is actually rendered (the default set while unset) and
    // keep the author's order, appending a newly ticked network at the end.
    const next = on ? (networks.includes(n) ? networks : [...networks, n]) : networks.filter((k) => k !== n)
    upd({ networks: next })
  }

  return (
    <>
      <div><label style={FIELD_LABEL}>Mode</label>
        <SelectField
          value={block.mode} fallback="follow"
          options={[['follow', 'Follow — link to our profiles'], ['share', 'Share — share this page']] as const}
          onChange={(m) => upd({ mode: m })}
        />
      </div>
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Style</label>
          <SelectField
            value={block.variant} fallback="plain"
            options={[['plain', 'Plain'], ['circle', 'Circle'], ['square', 'Square']] as const}
            onChange={(variant) => upd({ variant })}
          />
        </div>
        <div><label style={FIELD_LABEL}>Icon size (px)</label>
          <OptNumber value={block.iconSize} placeholder={20} min={8} max={96} onChange={(iconSize) => upd({ iconSize })} />
        </div>
      </div>
      <div><label style={FIELD_LABEL}>Alignment</label>
        <AlignPicker value={block.align || 'left'} onChange={(align) => upd({ align })} />
      </div>

      {showLinks && (
        <div style={mode === 'follow' ? undefined : DIMMED}>
          <label style={FIELD_LABEL}>Profile links</label>
          {mode !== 'follow' && <p style={{ ...HINT, marginBottom: 6 }}>Not used in share mode — kept for switching back to follow.</p>}
          <SocialLinkList links={links} onChange={(next) => upd({ links: next })} />
        </div>
      )}

      {showShare && (
        <div style={mode === 'share' ? { display: 'flex', flexDirection: 'column', gap: 8 } : { ...DIMMED, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {mode !== 'share' && <p style={HINT}>Share settings are not used in follow mode — kept for switching back to share.</p>}
          <div>
            <label style={FIELD_LABEL}>Share buttons</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
              {SOCIAL_NETWORKS.map(([n, label]) => (
                <label key={n} style={{ ...CHECK_ROW, fontSize: '0.8625rem' }}>
                  <input type="checkbox" checked={networks.includes(n)} onChange={(e) => toggleNetwork(n, e.target.checked)} />
                  {label}
                </label>
              ))}
              {unknownNets.map((n) => (
                <label key={n} style={{ ...CHECK_ROW, fontSize: '0.8625rem' }}>
                  <input type="checkbox" checked onChange={(e) => toggleNetwork(n, e.target.checked)} />
                  (unknown) {n}
                </label>
              ))}
            </div>
            {block.networks
              ? (
                <button
                  onClick={(e) => { e.stopPropagation(); upd({ networks: undefined }) }}
                  className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start', marginTop: 4 }}
                >Use the default set</button>
              )
              : <p style={HINT}>Showing the default set: X, Facebook, LinkedIn, WhatsApp, Email.</p>}
          </div>
          <div><label style={FIELD_LABEL}>Share text</label>
            <input value={block.shareTitle || ''} placeholder="The page title" onChange={(e) => upd({ shareTitle: optText(e.target.value) })} style={INPUT} />
          </div>
        </div>
      )}
    </>
  )
}

// ─── Lottie ──────────────────────────────────────────

export function LottieEditor({ block, project, onChange }: { block: LottieBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<LottieBlock>) => onChange({ ...block, ...patch })
  return (
    <>
      <div><label style={FIELD_LABEL}>Animation (.json or .svg)</label>
        <AssetPickerField project={project} value={block.url} accept=".json,application/json,.svg" onChange={(url) => upd({ url })} />
      </div>
      <div><label style={FIELD_LABEL}>Poster image</label>
        <AssetPickerField project={project} value={block.posterUrl} accept="image/*" onChange={(url) => upd({ posterUrl: optText(url) })} />
        <p style={HINT}>Shown while the player loads, on the static page, and to visitors who prefer reduced motion.</p>
      </div>
      <div><label style={FIELD_LABEL}>Height (px)</label>
        <OptNumber value={block.height} placeholder={280} min={40} max={1600} onChange={(height) => upd({ height })} />
      </div>
      <Check value={block.loop} fallback label="Loop" onChange={(loop) => upd({ loop })} />
      <Check value={block.autoplay} fallback label="Autoplay" onChange={(autoplay) => upd({ autoplay })} />
      <div><label style={FIELD_LABEL}>Caption</label>
        <input value={block.caption || ''} onChange={(e) => upd({ caption: optText(e.target.value) })} style={INPUT} />
      </div>
    </>
  )
}

// ─── Cookie consent ──────────────────────────────────

export function CookieConsentEditor({ block, onChange }: { block: CookieConsentBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<CookieConsentBlock>) => onChange({ ...block, ...patch })
  const cats = block.categories || []
  const setCat = (i: number, patch: Partial<ConsentCategory>) => upd({ categories: patchAt(cats, i, patch) })
  return (
    <>
      <div><label style={FIELD_LABEL}>Message</label>
        <textarea rows={3} value={block.message || ''} onChange={(e) => upd({ message: e.target.value })} style={TEXTAREA} />
      </div>
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Accept button</label>
          <input value={block.acceptLabel || ''} placeholder="Accept" onChange={(e) => upd({ acceptLabel: optText(e.target.value) })} style={INPUT} />
        </div>
        <div><label style={FIELD_LABEL}>Decline button</label>
          <input value={block.declineLabel || ''} placeholder="Decline" onChange={(e) => upd({ declineLabel: optText(e.target.value) })} style={INPUT} />
        </div>
      </div>
      <div><label style={FIELD_LABEL}>Preferences button</label>
        <input value={block.settingsLabel || ''} placeholder="Preferences" onChange={(e) => upd({ settingsLabel: optText(e.target.value) })} style={INPUT} />
        <p style={HINT}>Shown only when there are categories below.</p>
      </div>
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Policy link</label>
          <input value={block.policyHref || ''} placeholder="/privacy" onChange={(e) => upd({ policyHref: optText(e.target.value) })} style={INPUT} />
        </div>
        <div><label style={FIELD_LABEL}>Policy link text</label>
          <input value={block.policyLabel || ''} placeholder="Privacy policy" onChange={(e) => upd({ policyLabel: optText(e.target.value) })} style={INPUT} />
        </div>
      </div>
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Position</label>
          <SelectField
            value={block.position} fallback="bottom"
            options={[['bottom', 'Bottom bar'], ['top', 'Top bar'], ['bottom-left', 'Bottom left'], ['bottom-right', 'Bottom right']] as const}
            onChange={(position) => upd({ position })}
          />
        </div>
        <div><label style={FIELD_LABEL}>Storage key</label>
          <input value={block.storageKey || ''} placeholder="pg_consent" onChange={(e) => upd({ storageKey: optText(e.target.value) })} style={INPUT} />
        </div>
      </div>
      <p style={HINT}>Changing the storage key asks every returning visitor again.</p>

      <label style={{ ...FIELD_LABEL, marginTop: 4 }}>Categories</label>
      <p style={{ ...HINT, marginTop: 0 }}>Without categories the banner is accept / decline only.</p>
      <div style={LIST}>
        {cats.map((c, i) => (
          // Keyed by position, not `id`: the id is editable here, and keying on it
          // would remount the row (and drop focus) on every keystroke.
          <div key={i} style={SUBSECTION}>
            <ItemHeader
              label={`Category ${i + 1}`} i={i} count={cats.length}
              onMove={(d) => upd({ categories: moveItem(cats, i, d) })}
              onRemove={() => upd({ categories: cats.filter((_, k) => k !== i) })}
            />
            <div style={TWO_COL}>
              <input value={c.label} placeholder="Label" onChange={(e) => setCat(i, { label: e.target.value })} style={INPUT} />
              <input value={c.id} placeholder="id (e.g. analytics)" onChange={(e) => setCat(i, { id: e.target.value })} style={INPUT} />
            </div>
            <textarea rows={2} value={c.description || ''} placeholder="Description (optional)" onChange={(e) => setCat(i, { description: optText(e.target.value) })} style={TEXTAREA} />
            <label style={CHECK_ROW}>
              <input type="checkbox" checked={!!c.required} onChange={(e) => setCat(i, { required: e.target.checked })} />
              Required (always on)
            </label>
            <label style={{ ...CHECK_ROW, ...(c.required ? DIMMED : {}) }}>
              <input type="checkbox" checked={!!c.required || !!c.defaultOn} disabled={!!c.required} onChange={(e) => setCat(i, { defaultOn: e.target.checked })} />
              On by default
            </label>
          </div>
        ))}
        <AddButton
          label="Add category"
          onAdd={() => upd({ categories: [...cats, { id: newId('cat'), label: 'New category', defaultOn: false }] })}
        />
      </div>
      {cats.length > 0 && (
        <p style={HINT}>An analytics tag set to "requires consent" is released by the category with the same id.</p>
      )}
    </>
  )
}

// ─── Floating button ─────────────────────────────────

export function FloatingButtonEditor({ block, onChange }: { block: FloatingButtonBlock; project: string; onChange: (b: Block) => void }) {
  const upd = (patch: Partial<FloatingButtonBlock>) => onChange({ ...block, ...patch })
  const edge = block.position === 'right-center' || block.position === 'left-center'
  return (
    <>
      <ButtonSpecEditor
        label="Button"
        value={block.button}
        onChange={(button) => upd({ button })}
      />
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Position</label>
          <SelectField
            value={block.position} fallback="bottom-right"
            options={[['right-center', 'Right edge'], ['left-center', 'Left edge'], ['bottom-right', 'Bottom right'],
                      ['bottom-left', 'Bottom left'], ['top-right', 'Top right']] as const}
            onChange={(position) => upd({ position })}
          />
        </div>
        <div><label style={FIELD_LABEL}>Hide below (px)</label>
          <input type="number" value={block.hideBelow ?? ''} placeholder="never"
            onChange={(e) => upd({ hideBelow: e.target.value === '' ? undefined : Math.max(0, Number(e.target.value)) })}
            style={INPUT} />
        </div>
      </div>
      <div><label style={FIELD_LABEL}>Icon only below (px)</label>
        <input type="number" value={block.iconOnlyBelow ?? ''} placeholder="never"
          onChange={(e) => upd({ iconOnlyBelow: e.target.value === '' ? undefined : Math.max(0, Number(e.target.value)) })}
          style={INPUT} />
        <p style={HINT}>A round icon button on small screens (e.g. 640) — needs the button&apos;s icon. The label stays for screen readers.</p>
      </div>
      {edge && (
        <label style={CHECK_ROW}>
          <input type="checkbox" checked={!!block.vertical} onChange={(e) => upd({ vertical: e.target.checked })} />
          Turn the tab on its side
        </label>
      )}
      <div style={TWO_COL}>
        <div><label style={FIELD_LABEL}>Background</label>
          <input type="color" value={block.background || '#2563eb'} onChange={(e) => upd({ background: e.target.value })} style={{ ...INPUT, padding: 2, height: 32 }} />
        </div>
        <div><label style={FIELD_LABEL}>Text colour</label>
          <input type="color" value={block.textColor || '#ffffff'} onChange={(e) => upd({ textColor: e.target.value })} style={{ ...INPUT, padding: 2, height: 32 }} />
        </div>
      </div>
      <p style={HINT}>Pinned to the screen at runtime. Put it on the site header page to show it on every page.</p>
    </>
  )
}

// ─── Registry ────────────────────────────────────────

type MarketingEditor = (p: { block: Block; project: string; onChange: (b: Block) => void }) => JSX.Element

export const MARKETING_BLOCK_EDITORS: Partial<Record<Block['type'], MarketingEditor>> = {
  bento_grid: (p) => <BentoGridEditor {...p} block={p.block as BentoGridBlock} />,
  marquee: (p) => <MarqueeEditor {...p} block={p.block as MarqueeBlock} />,
  counter: (p) => <CounterEditor {...p} block={p.block as CounterBlock} />,
  before_after: (p) => <BeforeAfterEditor {...p} block={p.block as BeforeAfterBlock} />,
  carousel: (p) => <CarouselEditor {...p} block={p.block as CarouselBlock} />,
  team: (p) => <TeamEditor {...p} block={p.block as TeamBlock} />,
  steps: (p) => <StepsEditor {...p} block={p.block as StepsBlock} />,
  comparison_table: (p) => <ComparisonTableEditor {...p} block={p.block as ComparisonTableBlock} />,
  social_links: (p) => <SocialLinksEditor {...p} block={p.block as SocialLinksBlock} />,
  lottie: (p) => <LottieEditor {...p} block={p.block as LottieBlock} />,
  cookie_consent: (p) => <CookieConsentEditor {...p} block={p.block as CookieConsentBlock} />,
  floating_button: (p) => <FloatingButtonEditor {...p} block={p.block as FloatingButtonBlock} />,
}

export const MARKETING_EDITOR_TITLES: Partial<Record<Block['type'], string>> = {
  bento_grid: 'Bento grid',
  marquee: 'Marquee',
  counter: 'Counter',
  before_after: 'Before / after',
  carousel: 'Carousel',
  team: 'Team',
  steps: 'Steps',
  comparison_table: 'Comparison table',
  social_links: 'Social links',
  lottie: 'Animation',
  cookie_consent: 'Cookie consent',
  floating_button: 'Floating button',
}
